import { type PDFDocumentProxy, type RenderTask } from 'pdfjs-dist';
import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { cn } from '@apps/web/lib/utils';

export type PdfRenderState = 'rendered' | 'failed';

const MAX_OUTPUT_SCALE = 2;
const US_LETTER_ASPECT_RATIO = 8.5 / 11;

export function usePdfDocument(blob: Blob | null): {
    readonly pdf: PDFDocumentProxy | null;
    readonly failed: boolean;
} {
    const [loaded, setLoaded] = useState<{
        readonly blob: Blob;
        readonly pdf: PDFDocumentProxy | null;
    } | null>(null);
    useEffect(() => {
        if (blob === null) {
            return;
        }
        let cancelled = false;
        let destroy: (() => Promise<void>) | null = null;
        Promise.all([import('pdfjs-dist'), blob.arrayBuffer()])
            .then(([pdfjs, data]) => {
                if (cancelled) {
                    return null;
                }
                pdfjs.GlobalWorkerOptions.workerSrc = new URL(
                    'pdfjs-dist/build/pdf.worker.min.mjs',
                    import.meta.url,
                ).toString();
                // Canvas only, no scripting layer: PDF JavaScript never runs.
                const loadingTask = pdfjs.getDocument({ data });
                destroy = () => loadingTask.destroy();
                return loadingTask.promise;
            })
            .then(
                (pdf) => {
                    if (!cancelled && pdf !== null) {
                        setLoaded({ blob, pdf });
                    }
                },
                () => {
                    if (!cancelled) {
                        setLoaded({ blob, pdf: null });
                    }
                },
            );
        // Virtualized chat rows unmount constantly; each one owns a worker-side
        // document that must not outlive it.
        return () => {
            cancelled = true;
            void destroy?.();
        };
    }, [blob]);
    if (blob === null || loaded?.blob !== blob) {
        return { pdf: null, failed: false };
    }
    return { pdf: loaded.pdf, failed: loaded.pdf === null };
}

/** Renders one page while it is near the viewport; pass a stable callback. */
export function PdfPageCanvas({
    pdf,
    pageNumber,
    label,
    className,
    onRenderStateChange,
}: {
    readonly pdf: PDFDocumentProxy;
    readonly pageNumber: number;
    readonly label: string;
    readonly className?: string;
    readonly onRenderStateChange?: (state: PdfRenderState) => void;
}) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const { ref: inViewRef, inView } = useInView({
        rootMargin: '100% 0px',
        scrollMargin: '100% 0px',
    });
    const [aspectRatio, setAspectRatio] = useState(US_LETTER_ASPECT_RATIO);
    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas === null || !inView) {
            return;
        }
        let cancelled = false;
        let renderTask: RenderTask | null = null;
        pdf.getPage(pageNumber)
            .then((page) => {
                if (cancelled) {
                    return;
                }
                const pageViewport = page.getViewport({ scale: 1 });
                setAspectRatio(pageViewport.width / pageViewport.height);
                const cssWidth = canvas.clientWidth || pageViewport.width;
                const outputScale = Math.min(
                    window.devicePixelRatio || 1,
                    MAX_OUTPUT_SCALE,
                );
                const viewport = page.getViewport({
                    scale: (cssWidth / pageViewport.width) * outputScale,
                });
                canvas.width = Math.floor(viewport.width);
                canvas.height = Math.floor(viewport.height);
                renderTask = page.render({ canvas, viewport });
                return renderTask.promise;
            })
            .then(
                () => {
                    if (!cancelled) {
                        onRenderStateChange?.('rendered');
                    }
                },
                () => {
                    if (!cancelled) {
                        onRenderStateChange?.('failed');
                    }
                },
            );
        return () => {
            cancelled = true;
            renderTask?.cancel();
            // Safari otherwise keeps the backing store until garbage collection.
            canvas.width = 0;
            canvas.height = 0;
        };
    }, [inView, pdf, pageNumber, onRenderStateChange]);
    return (
        <div
            ref={inViewRef}
            role="img"
            aria-label={label}
            style={{ aspectRatio }}
            className={cn('w-full', className)}
        >
            <canvas
                ref={canvasRef}
                width={0}
                height={0}
                data-testid="pdf-page-canvas"
                className="block h-full w-full"
            />
        </div>
    );
}
